// ============================================================
// Chart helpers - Fase 5.
//
// Conversiones de tiempo, lectura de theme vars CSS, y transformaciones
// OHLC (Heikin Ashi).
//
// Convencion de tiempo:
//   - Backend devuelve "YYYY-MM-DD HH:mm" Eastern wall-clock.
//   - Lightweight Charts espera UTC seconds (UTCTimestamp = number).
//   - Conversion: parseamos el wall-clock como si estuviera en ET y
//     emitimos UTC seconds. Esto preserva la posicion correcta en el eje
//     temporal independientemente del browser locale.
// ============================================================

const ET = 'America/New_York';

/**
 * Offset (ms) de la timezone `tz` en el instante `utcMs`.
 * Mismo patron que apps/api/src/services/barchart.ts.
 */
function getTimezoneOffsetMs(utcMs: number, tz: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(new Date(utcMs));
  const o: Record<string, string> = {};
  for (const p of parts) o[p.type] = p.value;
  const hh = o.hour === '24' ? '00' : (o.hour ?? '00');
  const asUtc = Date.UTC(
    Number(o.year),
    Number(o.month) - 1,
    Number(o.day),
    Number(hh),
    Number(o.minute),
    Number(o.second ?? '0'),
  );
  return asUtc - utcMs;
}

/**
 * Convierte "YYYY-MM-DD HH:mm" (wall-clock ET) a UTC seconds para Lightweight.
 * Iteramos dos veces para que DST converja en bordes (mismo patron que el
 * helper de Barchart).
 */
export function etWallClockToUtcSeconds(wallClock: string): number {
  // "YYYY-MM-DD HH:mm" o "YYYY-MM-DD HH:mm:ss"
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})(?::(\d{2}))?$/.exec(wallClock);
  if (!m) return 0;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const hour = Number(m[4]);
  const minute = Number(m[5]);
  const second = Number(m[6] ?? '0');
  // Tratamos el wall-clock como UTC, restamos el offset que ET tenia en ese
  // instante. Iteramos 2 veces para que DST converja.
  let candidate = Date.UTC(year, month - 1, day, hour, minute, second);
  for (let i = 0; i < 2; i++) {
    const offset = getTimezoneOffsetMs(candidate, ET);
    candidate = Date.UTC(year, month - 1, day, hour, minute, second) - offset;
  }
  return Math.floor(candidate / 1000);
}

/**
 * Convierte un ISO UTC string a UTC seconds (Lightweight time).
 * Util para entryTime/exitTime de trades que vienen como ISO.
 */
export function isoToUtcSeconds(iso: string | null): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

// ============================================================
// Formatear UTC seconds (lo que devuelven nuestros helpers) como wall-clock
// ET para axis y tooltips. Lightweight Charts maneja todo como UTC
// internamente; con tickMarkFormatter + custom tooltips mostramos ET.
// ============================================================

const ET_AXIS_TIME_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: ET,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});
const ET_AXIS_DATE_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: ET,
  month: 'short',
  day: '2-digit',
});

/**
 * tickMarkFormatter para Lightweight v5: dado un UTC seconds (Time) y el
 * tipo de tick, devuelve la etiqueta en horario ET. La libreria muestra
 * "HH:mm" cuando el tick es intraday y "DD MMM" cuando cambia de dia.
 */
export function tickMarkFormatterET(time: number, tickMarkType: number): string {
  const d = new Date(time * 1000);
  // tickMarkType: 0=Year, 1=Month, 2=DayOfMonth, 3=Time, 4=TimeWithSeconds
  if (tickMarkType >= 3) {
    return ET_AXIS_TIME_FMT.format(d);
  }
  return ET_AXIS_DATE_FMT.format(d);
}

// ============================================================
// Theme: lectura de CSS vars HSL del :root (mismo patron que shadcn).
// CSS vars vienen como "240 10% 3.9%" (sin hsl()), las convertimos.
// ============================================================

/**
 * Lee una CSS var HSL (formato "H S% L%") y la devuelve como
 * string CSS valido "hsl(H, S%, L%)" para Lightweight Charts.
 * Si la var no existe, fallback al `defaultColor`.
 */
/**
 * Aplica alpha (0..1) a un color CSS en formato `hsl(H S% L%)` o `rgb(R G B)`.
 * Usa la syntax moderna `hsl(... / alpha)` que es CSS Color Module Level 4.
 *
 *   withAlpha('hsl(142 71% 65%)', 0.4) -> 'hsl(142 71% 65% / 0.4)'
 *
 * Lightweight Charts pasa el color a Canvas `addColorStop`, que rechaza
 * formatos invalidos. NO concatenar hex shortcut sobre hsl(): la app usa
 * tokens HSL, no hex.
 */
export function withAlpha(color: string, alpha: number): string {
  const m = /^(hsl|rgb)\(([^/)]+)(?:\s*\/\s*[^)]+)?\)$/i.exec(color.trim());
  if (!m) return color;
  const fn = m[1];
  const inner = m[2]?.trim() ?? '';
  return `${fn}(${inner} / ${alpha})`;
}

export function readThemeColor(varName: string, defaultColor: string): string {
  if (typeof window === 'undefined') return defaultColor;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  if (!raw) return defaultColor;
  return `hsl(${raw})`;
}

export interface ChartTheme {
  background: string;
  foreground: string;
  border: string;
  grid: string;
  success: string;
  destructive: string;
  primary: string;
  gold: string;
  muted: string;
}

/**
 * Lee toda la paleta del CSS theme actual. Llamar dentro de un effect
 * que reaccione al cambio de data-theme/dark class.
 */
export function getChartTheme(): ChartTheme {
  return {
    background: readThemeColor('--background', '#1a1a1a'),
    foreground: readThemeColor('--foreground', '#f5f1e8'),
    border: readThemeColor('--border', '#404040'),
    grid: readThemeColor('--border', '#404040'),
    success: readThemeColor('--success', '#4ade80'),
    destructive: readThemeColor('--destructive', '#f87171'),
    primary: readThemeColor('--primary', '#D87807'),
    gold: readThemeColor('--gold', '#d4af37'),
    muted: readThemeColor('--muted-foreground', '#b0b0b0'),
  };
}

// ============================================================
// Heikin Ashi: transformacion de candles OHLC clasica.
//
//   HA.close = (O+H+L+C)/4
//   HA.open  = (prev HA.open + prev HA.close)/2  [first: (O+C)/2]
//   HA.high  = max(H, HA.open, HA.close)
//   HA.low   = min(L, HA.open, HA.close)
// ============================================================

export interface OhlcCandle {
  time: number; // UTC seconds
  open: number;
  high: number;
  low: number;
  close: number;
}

export function toHeikinAshi(candles: OhlcCandle[]): OhlcCandle[] {
  if (candles.length === 0) return [];
  const out: OhlcCandle[] = [];
  let prev: OhlcCandle | null = null;
  for (const c of candles) {
    const haClose = (c.open + c.high + c.low + c.close) / 4;
    const haOpen = prev ? (prev.open + prev.close) / 2 : (c.open + c.close) / 2;
    const haHigh = Math.max(c.high, haOpen, haClose);
    const haLow = Math.min(c.low, haOpen, haClose);
    const ha: OhlcCandle = {
      time: c.time,
      open: haOpen,
      high: haHigh,
      low: haLow,
      close: haClose,
    };
    out.push(ha);
    prev = ha;
  }
  return out;
}

// ============================================================
// Days resolver: dado un SESSION_START ISO + opcional SESSION_END ISO,
// devuelve la lista de dias 'YYYYMMDD' (ET wall-clock) para query.
// ============================================================

export function daysBetween(startISO: string, endISO: string | null = null): string[] {
  const start = new Date(startISO);
  const end = endISO ? new Date(endISO) : new Date();
  if (!Number.isFinite(start.getTime())) return [];
  const days = new Set<string>();
  // Iteracion hour-by-hour para cubrir DST sin perder un dia.
  for (let d = new Date(start); d <= end; d = new Date(d.getTime() + 60 * 60_000)) {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: ET,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const parts = dtf.formatToParts(d);
    const o: Record<string, string> = {};
    for (const p of parts) o[p.type] = p.value;
    days.add(`${o.year}${o.month}${o.day}`);
  }
  return [...days].sort();
}
